import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AssessmentmanagerService } from 'src/app/services/assessments/assessmentmanager.service';

interface ScoreRow {
  id?: number;
  name: string;
  weight: number;
  examplePct: number;
  marks?: number;          // undefined for a brand-new assessment (no questions yet)
  optional?: boolean;
  quiz?: boolean;
  isThis?: boolean;
}

/**
 * "How the course score works", fed by the course's real assessments and pass score.
 * Mirrors ClassUtil.PassCourseMark: an assessment counts when it is not optional, not a lesson
 * quiz and has marks; each counts as (score %) × weight, divided by the sum of weights, with
 * equal weights when every weight is 0. Student scores are the only made-up part: the admin
 * types an example % for each assessment.
 * Demo rows are used only on the course editor when the course has no assessments yet.
 */
@Component({
  selector: 'app-scoring-explainer',
  templateUrl: './scoring-explainer.component.html',
  styleUrls: ['./scoring-explainer.component.css']
})
export class ScoringExplainerComponent implements OnInit, OnChanges {
  @Input() context: 'course' | 'assessment' = 'course';
  @Input() moduleId: number;
  @Input() classId: number;
  /** The assessment being edited (0 / empty when creating one). */
  @Input() assessmentId: number;
  /** Live values from the form around this component. */
  @Input() passScore: any;
  @Input() weight: any;
  @Input() optional: boolean;
  @Input() quiz: boolean;

  open = false;
  loading = false;
  isDemo = false;
  rows: ScoreRow[] = [];
  private savedPassScore: number = null;

  private static readonly EXAMPLE_PCTS = [80, 55, 70, 65, 90, 60, 75];

  constructor(private assessments: AssessmentmanagerService) {}

  async ngOnInit() {
    await this.load();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.weight || changes.optional || changes.quiz) && this.rows.length) {
      this.syncThisRow();
    }
  }

  private async load() {
    let summary: any = null;
    if (this.moduleId || this.classId) {
      this.loading = true;
      summary = await this.assessments.getCourseScoring(this.moduleId, this.classId).catch(() => null);
      this.loading = false;
    }
    this.savedPassScore = summary ? Number(summary.passScorePercentage) : null;
    const real: ScoreRow[] = (summary?.assessments ?? []).map((a: any, i: number) => ({
      id: a.id,
      name: a.name,
      weight: Number(a.weight) || 0,
      marks: Number(a.marks) || 0,
      optional: a.optional,
      quiz: a.quizAssessment,
      examplePct: ScoringExplainerComponent.EXAMPLE_PCTS[i % ScoringExplainerComponent.EXAMPLE_PCTS.length],
    }));

    if (this.context === 'assessment') {
      const id = Number(this.assessmentId) || 0;
      const mine = real.find(r => r.id === id && id > 0);
      if (mine) {
        mine.isThis = true;
        mine.name = 'This assessment';
      } else {
        real.push({ name: 'This assessment (new)', weight: 0, examplePct: 70, isThis: true });
      }
      this.rows = real;
      this.syncThisRow();
      return;
    }

    if (real.length > 0) {
      this.rows = real;
    } else {
      this.isDemo = true;
      this.rows = [
        { name: 'Weekly quiz', weight: 20, marks: 10, examplePct: 80 },
        { name: 'Assignment', weight: 30, marks: 20, examplePct: 50 },
        { name: 'Final exam', weight: 50, marks: 100, examplePct: 70 },
      ];
    }
  }

  /** The row for the assessment on screen follows the form, so the admin sees it change. */
  private syncThisRow() {
    const row = this.rows.find(r => r.isThis);
    if (!row) return;
    const w = Number(this.weight);
    if (this.weight !== null && this.weight !== undefined && this.weight !== '' && !isNaN(w)) row.weight = w;
    if (this.optional !== undefined) row.optional = !!this.optional;
    if (this.quiz !== undefined) row.quiz = !!this.quiz;
  }

  /** Why a row is left out, or null when it counts. */
  excludedReason(row: ScoreRow): string | null {
    if (row.quiz) return 'Lesson quiz';
    if (row.optional) return 'Optional';
    // a brand-new assessment is shown as counting: it will once its questions are added
    if (row.marks !== undefined && row.marks <= 0) return 'No questions yet';
    return null;
  }

  counts(row: ScoreRow): boolean {
    return this.excludedReason(row) === null;
  }

  get countedRows(): ScoreRow[] {
    return this.rows.filter(r => this.counts(r));
  }

  private get totalWeight(): number {
    return this.countedRows.reduce((t, r) => t + (Number(r.weight) || 0), 0);
  }

  /** The weight a row actually counts with: its own, or 1 each when every weight is 0. */
  used(row: ScoreRow): number {
    if (!this.counts(row)) return 0;
    return this.totalWeight > 0 ? (Number(row.weight) || 0) : 1;
  }

  share(row: ScoreRow): number {
    const sum = this.countedRows.reduce((t, r) => t + this.used(r), 0);
    return sum > 0 ? Math.round((this.used(row) / sum) * 100) : 0;
  }

  get score(): number {
    const sum = this.countedRows.reduce((t, r) => t + this.used(r), 0);
    if (sum <= 0) return 0;
    return Math.round(this.countedRows.reduce((t, r) => t + this.clampPct(r.examplePct) * this.used(r), 0) / sum);
  }

  get allWeightsZero(): boolean {
    return this.countedRows.length > 0 && this.totalWeight === 0;
  }

  /** The pass line: live value on the course editor, the saved course value elsewhere. */
  get effectivePass(): number {
    const live = this.context === 'course' ? Number(this.passScore) : NaN;
    const p = !isNaN(live) && this.passScore !== null && this.passScore !== '' ? live : (this.savedPassScore ?? 0);
    return Math.max(0, Math.min(Number(p) || 0, 100));
  }

  get noPassMark(): boolean {
    return this.effectivePass === 0;
  }

  get passed(): boolean {
    return this.countedRows.length === 0 || this.score >= this.effectivePass;
  }

  clampPct(v: any): number {
    const n = Number(v);
    return isNaN(n) ? 0 : Math.max(0, Math.min(n, 100));
  }
}
